import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

const serviceOptions = ["Residential", "Commercial", "Vacation Rental", "Not sure yet"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", service: "Residential", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! Betty will be in touch very soon.");
      setForm({ name: "", email: "", service: "Residential", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#C68A3A] py-3 text-[#241B2E] placeholder:text-[#5A2E14]/50 focus:outline-none focus:border-[#C1440E] transition-colors duration-300";

  return (
    <section id="contact" className="px-6 py-24 md:py-36 bg-[#F7E9CC] border-t border-[#C68A3A]/60" data-testid="contact-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease }}
            className="text-xs tracking-[0.25em] uppercase text-[#C1440E] font-semibold mb-6"
          >
            Get in touch
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
          >
            Let's get your space <span className="italic text-[#C1440E]">sparkling.</span>
          </motion.h2>
          <p className="text-[#5A2E14] mt-6 max-w-md leading-relaxed">
            Tell us a little about your space and we'll send over a friendly, no-pressure quote.
          </p>

          <div className="mt-10 space-y-4">
            <a href="mailto:bettybells0219@gmail.com" data-testid="contact-email-link" className="block group">
              <span className="text-xs uppercase tracking-wider text-[#5A2E14]">Email</span>
              <p className="font-serif text-2xl group-hover:text-[#C1440E] transition-colors duration-300">bettybells0219@gmail.com</p>
            </a>
            <a href="tel:9127046597" data-testid="contact-phone-link" className="block group">
              <span className="text-xs uppercase tracking-wider text-[#5A2E14]">Phone</span>
              <p className="font-serif text-2xl group-hover:text-[#C1440E] transition-colors duration-300">(912) 704-6597</p>
            </a>
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease }}
          className="space-y-7"
        >
          <div>
            <label className="text-sm text-[#5A2E14]">Your name</label>
            <input data-testid="contact-name-input" value={form.name} onChange={update("name")} placeholder="Jane Appleseed" className={inputCls} />
          </div>
          <div>
            <label className="text-sm text-[#5A2E14]">Email address</label>
            <input data-testid="contact-email-input" type="email" value={form.email} onChange={update("email")} placeholder="jane@email.com" className={inputCls} />
          </div>
          <div>
            <label className="text-sm text-[#5A2E14]">Service needed</label>
            <select data-testid="contact-service-select" value={form.service} onChange={update("service")} className={inputCls}>
              {serviceOptions.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm text-[#5A2E14]">Tell us about your space</label>
            <textarea data-testid="contact-message-input" value={form.message} onChange={update("message")} rows={4} placeholder="3-bed home, weekly cleaning..." className={`${inputCls} resize-none`} />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-btn"
            className="group inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-[#241B2E] text-[#EFD9AE] hover:bg-[#C1440E] hover:-translate-y-1 transition-[background-color,transform] duration-300 disabled:opacity-60 disabled:hover:translate-y-0"
          >
            <span className="text-sm font-medium">{loading ? "Sending..." : "Send my request"}</span>
            {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
